import React, { useEffect } from "react";
import { styled } from "../stitches.config";
import {
  AnyBlock,
  Block,
  BlockType,
  MenuBlock as MenuBlockType,
  User,
} from "../types";
import Icon, { IconType } from "../components/Icon";
import { Button } from "../components/Button";
import {
  blocksOpsRefAtom,
  blockYouJustCreatedAtom,
  currentUserAtom,
} from "../jotai/atoms";
import { useUpdateAtom } from "jotai/utils";
import { useFeatureFlags, useUserInfo } from "../liveblocks/maps";
import { useUpdateMyPresence } from "@liveblocks/react";
import { defaultCSS } from "./CssBlock";
import { defaultSearch } from "./ImageSearchBlock";
import { useSendAnalyticsEvent } from "../analytics/events";
import { useAtom } from "jotai";

export const BLOCK_TYPE_ICONS: Record<Block["type"], IconType | null> = {
  face: "FaceDock",
  web: "WebBlock",
  text: "TextBlock",
  "text/json": "TextBlock",
  link: "Link", // not used
  image: "ImgBlock",
  imageSearch: "ImageSearch",
  css: "Brush",
  draw: "DrawBlock",
  screen: "ScreenBlock",
  youtube: "Video",
  menu: null,
  svg: null,
};

export const BLOCK_TYPE_LABELS: Record<string, string> = {
  face: "Face Dock",
  web: "Browse Web [alpha]",
  text: "Write Text",
  "text/json": "Write Text",
  link: "Link", // not used
  draw: "Draw",
  image: "Upload Image",
  imageSearch: "Search for GIFs, Images",
  css: "Write Style Rules",
  screen: "Share Screen",
  youtube: "Watch YouTube Video",
};

const iconStyle = { width: 24, height: 24 };

function BlockTypeButton({
  type,
  label,
  hover,
  iconOnly,
  onClick,
  onHover,
}: {
  type: BlockType;
  label: string | undefined;
  hover: boolean;
  iconOnly: boolean;
  onClick: (type: BlockType) => void;
  onHover: (type: BlockType) => void;
}) {
  return (
    <SelectTypeButton
      key={type}
      style={{ margin: 0, borderRadius: 0 }}
      type="button"
      value={type}
      onMouseDown={(e: any) => {
        e.stopPropagation();
      }}
      onClick={() => onClick(type)}
      onMouseOver={() => onHover(type)}
      title={label}
      aria-label={label}
      // display artificial hover state
      hover={hover}
    >
      <ButtonLabel iconOnly={iconOnly}>
        <ButtonLabelIcon>
          <Icon style={iconStyle} icon={BLOCK_TYPE_ICONS[type!] as any} />{" "}
        </ButtonLabelIcon>

        {!iconOnly && <ButtonLabelText>{label}</ButtonLabelText>}
      </ButtonLabel>
    </SelectTypeButton>
  );
}

export function defaultBlockCreate(
  type: BlockType,
  user: User | undefined
): Partial<AnyBlock> {
  const newBlock: Partial<AnyBlock> = {
    type: type as any,
  };

  switch (newBlock.type) {
    case "text/json":
      newBlock.value = JSON.stringify({ type: "doc", content: [] });
      break;
    case "web":
      newBlock.value = "https://paint.js.org/";
      break;
    case "css":
      newBlock.value = !!user ? defaultCSS(user) : "";
      break;
    case "imageSearch":
      newBlock.value = "[]";
      newBlock.text = defaultSearch();
      newBlock.searchType = "gif-transparent";
      break;
    case "youtube":
      newBlock.value = "";
      break;
    default:
      newBlock.value = undefined;
  }
  return newBlock;
}

function MenuBlock({
  block,
  updateBlock,
}: {
  block: MenuBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
}) {
  const setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);
  const sendAnalyticsEvent = useSendAnalyticsEvent();
  const wrapperRef = React.useRef<HTMLUListElement | null>(null);
  const updateMyPresence = useUpdateMyPresence();
  const [blocksOpsRef] = useAtom(blocksOpsRefAtom);

  const [currentUser] = useAtom(currentUserAtom);
  const [user] = useUserInfo(block?.madeBy);
  const ignoreScrollEventRef = React.useRef(false);

  function onClick(type: BlockType) {
    if (type === "draw") {
      updateMyPresence({ drawMode: [[]] });
      if (blocksOpsRef.current.deleteBlock) {
        blocksOpsRef.current.deleteBlock(block.id);
      }
      return;
    }

    const newBlock = defaultBlockCreate(type, user);
    // Choosing a block type here is the equivalent of creating a new block for analytics
    if (newBlock.type) {
      sendAnalyticsEvent({ name: "blockCreated", type: newBlock.type });
    }

    setBlockYouJustCreated(block.id);
    updateBlock(newBlock);
  }
  function onHover(type: BlockType) {
    // used to display "artificial" hover state seen by other users
    updateBlock({
      value: type,
    });
  }

  // Sync scroll
  useEffect(() => {
    if (!block.scroll) return;
    if (wrapperRef.current) {
      if (currentUser?.id !== block.scroll.userId) {
        ignoreScrollEventRef.current = true;
        wrapperRef.current.scrollTop = block.scroll.value;
      }
    }
  }, [block.scroll, currentUser?.id]);

  const onScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (ignoreScrollEventRef.current) {
      ignoreScrollEventRef.current = false;
      return;
    }
    updateBlock({
      scroll: {
        value: (e.target as HTMLDivElement).scrollTop,
        userId: currentUser?.id ?? "",
      },
    });
  };

  const [featureFlags] = useFeatureFlags();

  let blockTypes = [
    "text/json",
    "face",
    "imageSearch",
    "draw",
    "screen",
    "image",
    "youtube",
    "css",
    "web",
  ];
  // Web block turned off completely until we can get it working
  if (true || !featureFlags?.webBlock) {
    blockTypes = blockTypes.filter((t) => t !== "web");
  }
  const num_rows = blockTypes.length;
  const iconOnly = block.size.width < 6;
  return (
    <ButtonWrapper
      ref={wrapperRef}
      style={{ gridTemplateRows: `repeat(${num_rows}, 1fr)` }}
      onMouseLeave={() => onHover("menu")}
      onScroll={onScroll}
      data-cursor={"pointer"}
    >
      {blockTypes.slice(0, num_rows).map((type) => (
        <BlockTypeButton
          key={type}
          type={type as BlockType}
          label={BLOCK_TYPE_LABELS[type!]}
          hover={block.value === type}
          iconOnly={iconOnly}
          onClick={onClick}
          onHover={onHover}
        />
      ))}
    </ButtonWrapper>
  );
}

export default MenuBlock;

const ButtonWrapper = styled("ul", {
  display: "grid",
  gridAutoFlow: "column",
  gridGap: "2px",
  margin: "2px",
  overflow: "auto",
});

const SelectTypeButton = styled(Button, {
  borderRadius: "0",
  width: "100%",
  height: "100%",
  display: "grid",
  gridTemplateColumns: "1fr",
  gridAutoFlow: "column",
});

const ButtonLabel = styled("div", {
  borderRadius: "0",
  display: "grid",
  width: "100%",
  height: "100%",
  variants: {
    iconOnly: {
      true: {
        gridTemplateColumns: "1fr",
      },
      false: {
        gridTemplateColumns: "64px 1fr",
      },
    },
  },
});

const ButtonLabelText = styled("span", {
  textAlign: "left",
  display: "inline-flex",
  alignItems: "center",
});

const ButtonLabelIcon = styled(ButtonLabelText, {
  justifyContent: "center",
});
